import { CollectionGroupType } from "../../server/controller/CollectionGroupsController";
import {
  CategoryDto,
  PICGroup,
  PICGroupWithCategories,
  CollectionDto,
  DataMapDto,
  DisclosureDto,
  ReceivedDto,
} from "../../server/dto/datamap/DataMapDto";
import { PICSurveyClassification } from "../../server/dto/PersonalInformationOptionsDto";
import _ from "lodash";
import { UUIDString } from "../../server/Types";
import { useMemo } from "react";
import hash from "object-hash";

/**
 * Allows for filtering by collection group type, a specific collection group(s), PIC survey classifications,
 * a specific PIC group(s), or only include sensitive inferences.
 */
export type DataMapCollectedFilter = {
  collectionGroupType?: CollectionGroupType[] | CollectionGroupType;
  collectionGroupId?: UUIDString[] | UUIDString;
  surveyClassification?: PICSurveyClassification[] | PICSurveyClassification;
  picGroupId?: UUIDString[] | UUIDString;
  exclude?: {
    collectionGroupType?: CollectionGroupType[] | CollectionGroupType;
  };

  /**
   * Only include collection groups that have the Sensitive Inferences PIC collected from it. (Note that this still includes all other PICs collected from those collection group.)
   */
  onlyIncludeSensitiveInferences?: boolean;
};

export type DataMapDisclosedFilter = {
  collectionGroupType?: CollectionGroupType[] | CollectionGroupType;
  collectionGroupId?: UUIDString[] | UUIDString;
  vendorId?: UUIDString[] | UUIDString;
  exclude?: {
    collectionGroupType?: CollectionGroupType[] | CollectionGroupType;
  };
};

export type DataMapReceivedFilter = {
  vendorId?: UUIDString[] | UUIDString;
  collectionGroupId?: UUIDString[] | UUIDString;
};

//#region collection

/**
 * Returns a subset of `dataMap.collection` based on the given filters.
 */
export const filterDataMapCollection = (
  dataMap: DataMapDto,
  filters: DataMapCollectedFilter = {},
): CollectionDto[] => {
  if (!dataMap) {
    return [];
  }
  let collected = dataMap.collection.slice();

  if (filters.onlyIncludeSensitiveInferences) {
    const inferencesId = Object.values(dataMap.categories).find(
      (cat) => cat.key === "inferences-based-on-sensitive-information",
    )?.id;
    if (inferencesId) {
      const filteredCollectionGroups = Object.values(dataMap.collectionGroups)
        .filter((cg) => collected.some((c) => c.groupId == cg.id && c.picId == inferencesId))
        .map((cg) => cg.id);
      collected = collected.filter((c) => filteredCollectionGroups.includes(c.groupId));
    }
  }

  if (filters.exclude?.collectionGroupType) {
    const excludeFilter = [filters.exclude.collectionGroupType].flat();
    collected = collected.filter((c) => {
      const groupType = dataMap.collectionGroups[c.groupId]?.type;
      return !excludeFilter.includes(groupType);
    });
  }

  if (filters.collectionGroupType) {
    const filter = [filters.collectionGroupType].flat();
    collected = collected.filter((c) => {
      const groupType = dataMap.collectionGroups[c.groupId]?.type;
      return filter.includes(groupType);
    });
  }

  if (filters.collectionGroupId) {
    const filter = [filters.collectionGroupId].flat();
    collected = collected.filter((c) => filter.includes(c.groupId));
  }

  if (filters.picGroupId) {
    const filter = [filters.picGroupId].flat();
    collected = collected.filter((c) => filter.includes(dataMap.categories[c.picId]?.picGroupId));
  }

  if (filters.surveyClassification) {
    const filter = [filters.surveyClassification].flat();
    collected = collected.filter((c) => {
      return filter.includes(dataMap.categories[c.picId]?.surveyClassification);
    });
  }

  return collected;
};

/**
 * Returns a list of collected PICs after applying the given filters to the data map collection.
 */
export const collectedPICsFromFilteredCollection = (
  dataMap: DataMapDto,
  filters: DataMapCollectedFilter = {},
): CategoryDto[] => {
  if (!dataMap) {
    return [];
  }
  const collected = filterDataMapCollection(dataMap, filters);
  const picIds = _.uniq(collected.map((c) => c.picId));
  return picIds
    .map((picId) => dataMap.categories[picId])
    .filter((x) => x)
    .sort((a, b) => {
      // The assumption here is that a PIC group will never
      // have more than 1000 categories.
      const leftOrder = a.picGroupOrder * 1000 + a.order;
      const rightOrder = b.picGroupOrder * 1000 + b.order;
      return leftOrder - rightOrder;
    });
};

/**
 * Returns the list of collected PIC groups after applying the given filters to the data map collection.
 */
export const collectedPICGroupsFromFilteredCollection = (
  dataMap: DataMapDto,
  filters: DataMapCollectedFilter = {},
): PICGroupWithCategories[] => {
  return picGroupsWithCategoriesFromPics(
    dataMap,
    collectedPICsFromFilteredCollection(dataMap, filters),
  );
};

export const useDataMapCollected = (dataMap: DataMapDto, filters: DataMapCollectedFilter = {}) => {
  return useMemo(() => filterDataMapCollection(dataMap, filters), [dataMap, hash(filters)]);
};

export const useDataMapCollectedPIC = (
  dataMap: DataMapDto,
  filters: DataMapCollectedFilter = {},
) => {
  return useMemo(
    () => collectedPICsFromFilteredCollection(dataMap, filters),
    [dataMap, hash(filters)],
  );
};

export const useDataMapCollectedPICIds = (
  dataMap: DataMapDto,
  filters: DataMapCollectedFilter = {},
) => {
  return useMemo(
    () => collectedPICsFromFilteredCollection(dataMap, filters).map((pic) => pic.id),
    [dataMap, hash(filters)],
  );
};

export const useDataMapCollectedPICGroups = (
  dataMap: DataMapDto,
  filters: DataMapCollectedFilter = {},
) => {
  return useMemo(
    () => collectedPICGroupsFromFilteredCollection(dataMap, filters),
    [dataMap, hash(filters)],
  );
};

//#endregion

//#region disclosed

/**
 * Returns a subset of `dataMap.disclosure` based on the given filters.
 */
export const filterDataMapDisclosed = (
  dataMap: DataMapDto,
  filters: DataMapDisclosedFilter = {},
): DisclosureDto[] => {
  if (!dataMap) {
    return [];
  }
  let disclosed = dataMap.disclosure.slice();

  if (filters.exclude?.collectionGroupType) {
    const excludeFilter = [filters.exclude.collectionGroupType].flat();
    disclosed = disclosed.filter((c) => {
      const groupType = dataMap.collectionGroups[c.groupId]?.type;
      return !excludeFilter.includes(groupType);
    });
  }

  if (filters.collectionGroupType) {
    const filter = [filters.collectionGroupType].flat();
    disclosed = disclosed.filter((c) => {
      const groupType = dataMap.collectionGroups[c.groupId]?.type;
      return filter.includes(groupType);
    });
  }

  if (filters.collectionGroupId) {
    const filter = [filters.collectionGroupId].flat();
    disclosed = disclosed.filter((c) => filter.includes(c.groupId));
  }

  if (filters.vendorId) {
    const filter = [filters.vendorId].flat();
    disclosed = disclosed.filter((c) => filter.includes(c.vendorId));
  }

  return disclosed;
};

/**
 * Returns the list of disclosed PICs after applying the given filters to the data map disclosure.
 */
export const disclosedPICsFromFilteredDisclosed = (
  dataMap: DataMapDto,
  filters: DataMapDisclosedFilter = {},
): CategoryDto[] => {
  const disclosed = filterDataMapDisclosed(dataMap, filters);
  const picIds = _.uniq(disclosed.map((c) => c.picId));
  return picIds.map((picId) => dataMap.categories[picId]).filter((x) => x);
};

/**
 * Returns the list of disclosed PIC groups after applying the given filters to the data map disclosure.
 */
export const disclosedPICGroupsFromFilteredDisclosed = (
  dataMap: DataMapDto,
  filters: DataMapDisclosedFilter = {},
): PICGroupWithCategories[] => {
  return picGroupsWithCategoriesFromPics(
    dataMap,
    disclosedPICsFromFilteredDisclosed(dataMap, filters),
  );
};

export const useDataMapDisclosed = (dataMap: DataMapDto, filters: DataMapDisclosedFilter = {}) => {
  return useMemo(() => filterDataMapDisclosed(dataMap, filters), [dataMap, hash(filters)]);
};

export const useDataMapDisclosedPIC = (
  dataMap: DataMapDto,
  filters: DataMapDisclosedFilter = {},
) => {
  return useMemo(
    () => disclosedPICsFromFilteredDisclosed(dataMap, filters),
    [dataMap, hash(filters)],
  );
};

export const useDataMapDisclosedPICGroups = (
  dataMap: DataMapDto,
  filters: DataMapDisclosedFilter = {},
) => {
  return useMemo(
    () => disclosedPICGroupsFromFilteredDisclosed(dataMap, filters),
    [dataMap, hash(filters)],
  );
};

//#endregion

//#region received

/**
 * Returns a subset of `dataMap.received` based on the given filters.
 */
export const filterDataMapReceived = (
  dataMap: DataMapDto,
  filters: DataMapReceivedFilter = {},
): ReceivedDto[] => {
  if (!dataMap) {
    return [];
  }
  let received = dataMap.received.slice();

  if (filters.collectionGroupId) {
    const filter = [filters.collectionGroupId].flat();
    received = received.filter((r) => filter.includes(r.groupId));
  }

  if (filters.vendorId) {
    const filter = [filters.vendorId].flat();
    received = received.filter((r) => filter.includes(r.vendorId));
  }

  return received;
};

/**
 * Returns a list of received PICs after applying the given filters to the data map received.
 */
export const receivedPICsFromFilteredReceived = (
  dataMap: DataMapDto,
  filters: DataMapReceivedFilter = {},
): CategoryDto[] => {
  const received = filterDataMapReceived(dataMap, filters);
  return received.map((r) => dataMap.categories[r.picId]);
};

/**
 * Returns the list of received PIC groups after applying the given filters to the data map received.
 */
export const receivedPICGroupsFromFilteredReceived = (
  dataMap: DataMapDto,
  filters: DataMapReceivedFilter = {},
): PICGroupWithCategories[] => {
  return picGroupsWithCategoriesFromPics(
    dataMap,
    receivedPICsFromFilteredReceived(dataMap, filters),
  );
};

export const useDataMapReceived = (dataMap: DataMapDto, filters: DataMapReceivedFilter = {}) => {
  return useMemo(() => filterDataMapReceived(dataMap, filters), [dataMap, hash(filters)]);
};

export const useDataMapReceivedPIC = (dataMap: DataMapDto, filters: DataMapReceivedFilter = {}) => {
  return useMemo(
    () => receivedPICsFromFilteredReceived(dataMap, filters),
    [dataMap, hash(filters)],
  );
};

export const useDataMapReceivedPICGroups = (
  dataMap: DataMapDto,
  filters: DataMapReceivedFilter = {},
) => {
  return useMemo(
    () => receivedPICGroupsFromFilteredReceived(dataMap, filters),
    [dataMap, hash(filters)],
  );
};

//#endregion

//#region internal helpers

/**
 * Returns a list of `PICGroupWithCategories` for a given list of PICs.
 */
const picGroupsWithCategoriesFromPics = (
  dataMap: DataMapDto,
  pics: CategoryDto[],
): PICGroupWithCategories[] => {
  if (!dataMap) {
    return [];
  }
  const groupIds = _.uniq(pics.map((c) => c.picGroupId));
  const groups = groupIds
    .map((groupId) => dataMap.picGroups[groupId])
    .filter((x) => x)
    .sort((a, b) => a.order - b.order);
  return addCategoriesToPICGroups(dataMap, groups, pics);
};

/**
 * Converts a list of `PICGroup` into a list of `PICGroupWithCategories`, optionally only including a subset of PICs for all groups.
 */
const addCategoriesToPICGroups = (
  dataMap: DataMapDto,
  picGroups: PICGroup[],
  validPics?: CategoryDto[],
): PICGroupWithCategories[] => {
  let validPicIds = validPics ? validPics.map((c) => c.id) : Object.keys(dataMap.categories);
  return picGroups.map((group) => {
    const categories = Object.values(dataMap.categories).filter(
      (pic) => pic.picGroupId == group.id && validPicIds.includes(pic.id),
    );
    const clone = _.cloneDeep(group) as PICGroupWithCategories;
    clone.categories = categories;
    return clone;
  });
};

//#endregion
